import styled, { css } from 'styled-components'

import { Colors, mediaQueries } from '@fourthwall/components'

import { DASHBOARD_STYLED_CONSTS } from '../Dashboard/consts'

export const Container = styled.ol`
  margin-left: -${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_BOTTOM}px;
  margin-right: -${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_BOTTOM}px;

  ${mediaQueries.tablet} {
    margin-left: unset;
    margin-right: unset;
  }
`

export const ItemArrow = styled.div`
  margin-left: auto;
  display: none;

  color: ${Colors.GS600};
`

export const ItemCompletedButtonContainer = styled.div`
  margin-left: 16px;
  font-size: 14px;
  line-height: 150%;
  display: none;

  ${mediaQueries.tablet} {
    font-size: 16px;
  }
`

export const ItemContainer = styled.li<{
  $completed: boolean
  $open: boolean
  $isNextOpen: boolean
  $last: boolean
}>`
  list-style: none;
  width: 100%;
  padding: var(--space-400);

  display: flex;
  align-items: center;
  position: relative;

  border-top: 1px solid ${Colors.GS400};
  border-bottom: 1px solid ${Colors.GS400};

  ${mediaQueries.tablet} {
    padding: var(--space-500);

    border-left: 1px solid ${Colors.GS400};
    border-right: 1px solid ${Colors.GS400};
  }

  &:first-child {
    border-top-left-radius: var(--radius-brand);
    border-top-right-radius: var(--radius-brand);
  }

  &:last-child {
    border-bottom-left-radius: var(--radius-brand);
    border-bottom-right-radius: var(--radius-brand);
  }

  &:not(:last-child) {
    border-bottom: none;
  }

  ${({ $completed, $open }) =>
    !$completed &&
    !$open &&
    css`
      padding: var(--space-300) var(--space-400);

      ${mediaQueries.tablet} {
        padding: var(--space-350) var(--space-500);
      }

      &:hover {
        cursor: pointer;

        background: ${Colors.GS200};

        ${ItemTitle} {
          color: ${Colors.GS1000};
        }

        ${ItemNumber} {
          background: color-mix(in srgb, ${Colors.GS1000} 8%, transparent);
        }

        ${ItemArrow} {
          display: block;
        }
      }
    `}

  ${({ $completed }) =>
    $completed &&
    css`
      &:hover {
        ${ItemCompletedButtonContainer} {
          display: block;
        }
      }
    `}
`

export const ItemNumberContainer = styled.div`
  margin-right: 12px;

  ${mediaQueries.tablet} {
    align-self: flex-start;
    margin-right: 20px;
  }
`

export const ItemNumber = styled.div<{ $appearance: 'success' | 'active' | 'normal' }>`
  width: 20px;
  height: 20px;
  border-radius: 100px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  line-height: 130%;
  letter-spacing: 0.1px;

  color: ${Colors.GS600};

  ${mediaQueries.tablet} {
    width: 24px;
    height: 24px;
    font-size: 14px;
    line-height: 150%;
  }

  ${({ $appearance }) =>
    $appearance === 'success' &&
    css`
      background: ${Colors.G300};
      color: ${Colors.GS0};
    `};

  ${({ $appearance }) =>
    $appearance === 'active' &&
    css`
      background: ${Colors.GS1000};
      color: ${Colors.GS0};
      font-weight: 600;
    `};

  ${({ $appearance }) =>
    $appearance === 'normal' &&
    css`
      background: color-mix(in srgb, ${Colors.GS1000} 5%, transparent);
      color: ${Colors.GS600};
      font-weight: 400;
    `};
`

export const ItemMain = styled.div`
  flex: 1;
  min-width: 0;
`

export const ItemRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;

  ${mediaQueries.tablet} {
    flex-direction: row;
  }
`

export const ItemHeader = styled.div<{ $float: boolean }>`
  display: grid;
  gap: var(--space-400);

  ${mediaQueries.tablet} {
    gap: var(--space-500);

    ${({ $float }) =>
      $float &&
      css`
        display: flex;
        align-items: flex-start;

        > :first-child {
          flex: 1;
        }
      `};
  }
`

export const ItemTitleWrapper = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
`

export const ItemTitle = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;

  color: var(--text-secondary);
  font-family: var(--font-family-body);
  font-size: var(--font-size-400);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-400);
  letter-spacing: var(--font-letter-spacing-normal);

  ${({ $active }) =>
    $active &&
    css`
      color: var(--text-primary);
      font-family: var(--font-family-heading);
      font-size: var(--font-size-500);
      font-weight: var(--font-weight-semibold);
      line-height: var(--font-line-height-400);
      letter-spacing: var(--font-letter-spacing-denser);
    `};

  ${mediaQueries.tablet} {
    font-size: 16px;

    ${({ $active }) =>
      $active &&
      css`
        font-weight: 600;
        font-size: 20px;
        line-height: 28px;
      `};
  }
`

export const ItemDescription = styled.div`
  margin-top: var(--space-200);
  color: var(--text-secondary);
  font-family: var(--font-family-body);
  font-size: var(--font-size-400);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-400);
  letter-spacing: var(--font-letter-spacing-normal);

  ${mediaQueries.tablet} {
    font-size: 16px;
  }
`

export const ItemFooter = styled.div<{ $float: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;

  ${mediaQueries.tablet} {
    ${({ $float }) =>
      $float &&
      css`
        display: flex;
        position: relative;
        top: -4px;
        flex-direction: row-reverse;
      `};
  }
`

export const ItemSkipButton = styled.button`
  cursor: pointer;

  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: ${Colors.GS600};
  text-decoration: underline;

  &:hover {
    color: ${Colors.GS1000};
  }
`

export const ItemImage = styled.img`
  width: 188px;
  height: auto;
  align-self: center;
`
